import { gql } from '@urql/core';
import { createQueryKeys } from '@lukemorales/query-key-factory';

export const goalsFeatureKeys = createQueryKeys('goalsFeature', {
  goalList: ({ siteId, search, limit, order, sort }) => [{ siteId, search, limit, order: { field: order, sort } }],
  goalSimpleList: ({ siteId }) => [{ siteId }],
  goalDetail: ({ id, presetDate, startDate, endDate }) => [{ id, presetDate, startDate, endDate }],
  goalDistribution: ({ id, presetDate, startDate, endDate }) => [{ id, presetDate, startDate, endDate }],
  goalCounts: ({ id, presetDate, startDate, endDate }) => [{ id, presetDate, startDate, endDate }],
  goalSessionsCount: ({ id, presetDate, startDate, endDate }) => [{ id, presetDate, startDate, endDate }],
  goalPageConversions: ({ id, userId, siteId, startDate, endDate, presetDate, search, page, perPage, order }) => [
    { id, userId, siteId, startDate, endDate, presetDate, search, page, perPage, order },
  ],
});

export const goalListQuery = ({ siteId, search, limit, order, sort }) => ({
  ...goalsFeatureKeys.goalList({ siteId, search, limit, order, sort }),
  getNextPageParam: (lastPage) => (lastPage.goalList.hasNextPage ? lastPage.goalList.nextPageCursor : undefined),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalList($siteId: Int, $cursor: String, $search: String, $limit: Int, $order: GoalListOrder) {
        goalList(site: $siteId, cursor: $cursor, search: $search, limit: $limit, order: $order) {
          list {
            __typename
            triggers {
              id
              name
              type
              valueType
              autoName
              fixedValue {
                currency
                worth
              }
              elementValue {
                currency
                condition {
                  elementSelectorType
                  onAnyPage
                  onPage
                  pageUrl
                  selectorForDesktop
                  selectorForPhone
                  selectorForTablet
                  trackingOption
                  wildcardUrl
                }
              }
              filter
            }
            id
            name
            abTestsCount
            abTestsCountRunning
            abTestsCountStopped
            abTestsCountActive
            abTestsCountTotal
            conversionsCount
            createdAt
            createdBy {
              id
              login
              name
            }
            displayData
            icon
            permissions {
              canView
              canEdit
              canDelete
              canShare
            }
            sessionsCount
            uiMode
          }
          hasNextPage
          nextPageCursor
        }
      }
    `,
  },
});

export const goalSimpleListQuery = ({ siteId }) => ({
  ...goalsFeatureKeys.goalSimpleList({ siteId }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalSimpleList($siteId: Int!) {
        goalSimpleList(site: $siteId) {
          id
          siteId
          name
          hasValue
        }
      }
    `,
  },
});

export const GOAL_NAME_ONLY = gql`
  query GoalDetail($id: Int!) {
    goalDetail(id: $id) {
      id
      name
    }
  }
`;

export const goalDetailQuery = ({ id, startDate, endDate, presetDate }) => ({
  ...goalsFeatureKeys.goalDetail({ id, startDate, endDate, presetDate }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalDetail($id: Int!, $startDate: Int, $endDate: Int, $presetDate: String) {
        goalDetail(id: $id, startDate: $startDate, endDate: $endDate, presetDate: $presetDate) {
          __typename
          id
          name
          abTestsCount
          abTestsCountRunning
          abTestsCountStopped
          abTestsCountActive
          abTestsCountTotal
          autoName
          createdAt
          createdBy {
            id
            name
            login
          }
          icon
          deletedAt
          displayData
          conversionsCount
          permissions {
            canView
            canEdit
            canDelete
            canShare
          }
          purpose
          triggers {
            autoName
            createdAt
            deletedAt
            filter
            id
            name
            uiMode
            valueType
            type
            fixedValue {
              worth
              currency
            }
            elementValue {
              currency
              condition {
                trackingOption
                elementSelectorType
                pageUrl
                wildcardUrl
                onPage
                onAnyPage
                selectorForDesktop
                selectorForTablet
                selectorForPhone
              }
            }
          }
          siteId
          syncValues
          hasValue
          totalValue {
            displayWorth
            displayCurrency
          }
          abTests(startDate: $startDate, endDate: $endDate, presetDate: $presetDate) {
            id
            duration
            publishedAt
            stoppedAt
            createdAt
            createdByUserName
            name
            pageUrl
            status
            type
            totalConversions
            totalVisitors
            variants {
              id
              redirectUrl
              variantName
              results {
                conversions
              }
            }
            variantsCount
          }
        }
      }
    `,
  },
});

export const goalPageConversionsQuery = ({
  id,
  userId,
  siteId,
  startDate,
  endDate,
  presetDate,
  search,
  page,
  perPage,
  order,
}) => ({
  ...goalsFeatureKeys.goalPageConversions({
    id,
    userId,
    siteId,
    startDate,
    endDate,
    presetDate,
    search,
    page,
    perPage,
    order,
  }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalPageConversions(
        $id: Int!
        $userId: Int!
        $siteId: Int!
        $startDate: Int
        $endDate: Int
        $presetDate: String
        $search: String
        $page: Int
        $perPage: Int
        $order: GoalPageConversionsOrder
      ) {
        goalPageConversions(
          id: $id
          userId: $userId
          siteId: $siteId
          startDate: $startDate
          endDate: $endDate
          presetDate: $presetDate
          search: $search
          page: $page
          perPage: $perPage
          order: $order
        ) {
          list {
            pageUrl
            totalConversions
            totalVisitors
            totalValue
          }
          meta {
            page
            perPage
            total
            totalPages
          }
        }
      }
    `,
  },
});

export const goalDistributionQuery = ({ id, presetDate, startDate, endDate }) => ({
  ...goalsFeatureKeys.goalDistribution({ id, presetDate, startDate, endDate }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalDistribution($id: Int!, $startDate: Int, $endDate: Int, $presetDate: String) {
        goalDistribution(id: $id, startDate: $startDate, endDate: $endDate, presetDate: $presetDate) {
          conversionsCount
          uniqueConversionsCount
          date
          sessionsCount
          totalValue
          granularity
          triggers {
            id
            date
            conversionsCount
            sessionsCount
            uniqueConversionsCount
            uniqueSessionsCount
          }
        }
      }
    `,
  },
});

export const goalCountsQuery = ({ id, presetDate, startDate, endDate }) => ({
  ...goalsFeatureKeys.goalCounts({ id, presetDate, startDate, endDate }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalCounts($id: Int!, $presetDate: String, $startDate: Int, $endDate: Int) {
        goalCounts(id: $id, presetDate: $presetDate, startDate: $startDate, endDate: $endDate) {
          conversionsCount
          sessionsCount
          uniqueConversionsCount
          uniqueSessionsCount
          totalValue
          triggers {
            id
            conversionsCount
            sessionsCount
            uniqueConversionsCount
            uniqueSessionsCount
            totalValue
          }
        }
      }
    `,
  },
});

export const goalSessionsCountQuery = ({ id, presetDate, startDate, endDate }) => ({
  ...goalsFeatureKeys.goalSessionsCount({ id, presetDate, startDate, endDate }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalSessionsCount($id: Int!, $startDate: Int, $endDate: Int, $presetDate: String) {
        goalSessionsCount(id: $id, startDate: $startDate, endDate: $endDate, presetDate: $presetDate)
      }
    `,
  },
});
